import { Editor } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";

/**
 * Compact rich-text editor for page section body fields.
 * Supports toggling between WYSIWYG and raw HTML editing.
 *
 * Usage:
 *   <div x-data="sectionRichText(initialHtml, onChangeFn)">
 *     <div x-ref="editorArea"></div>
 *   </div>
 */
export default function registerSectionEditor(Alpine) {
  Alpine.data("sectionRichText", function (initialContent, onChangeFn) {
    return {
      editor: null,
      htmlMode: false,
      rawHtml: initialContent || "",

      init() {
        this.$nextTick(() => this.createEditor());
      },

      createEditor() {
        const el = this.$refs.editorArea;
        if (!el) return;

        this.editor = new Editor({
          element: el,
          extensions: [
            StarterKit.configure({ heading: { levels: [2, 3] } }),
            Underline,
            Link.configure({ openOnClick: false })
          ],
          content: this.rawHtml,
          editorProps: {
            attributes: {
              class: "tiptap-content prose prose-sm dark:prose-invert max-w-none focus:outline-none min-h-[120px] p-3"
            }
          },
          onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            this.rawHtml = html;
            if (typeof onChangeFn === "function") onChangeFn(html);
          }
        });
      },

      toggleMode() {
        if (this.htmlMode) {
          // HTML → Rich Text: push textarea content into editor
          if (this.editor) {
            this.editor.commands.setContent(this.rawHtml);
          }
        } else {
          // Rich Text → HTML: read current editor HTML
          if (this.editor) {
            this.rawHtml = this.editor.getHTML();
          }
        }
        this.htmlMode = !this.htmlMode;
      },

      onRawInput(value) {
        this.rawHtml = value;
        if (typeof onChangeFn === "function") onChangeFn(value);
      },

      // ── Toolbar actions ────────────────────────────────────
      bold() {
        this.editor?.chain().focus().toggleBold().run();
      },
      italic() {
        this.editor?.chain().focus().toggleItalic().run();
      },
      underline() {
        this.editor?.chain().focus().toggleUnderline().run();
      },
      heading(level) {
        this.editor?.chain().focus().toggleHeading({ level }).run();
      },
      bulletList() {
        this.editor?.chain().focus().toggleBulletList().run();
      },
      orderedList() {
        this.editor?.chain().focus().toggleOrderedList().run();
      },
      blockquote() {
        this.editor?.chain().focus().toggleBlockquote().run();
      },
      link() {
        const url = prompt("URL:");
        if (url) this.editor?.chain().focus().setLink({ href: url }).run();
      },
      hr() {
        this.editor?.chain().focus().setHorizontalRule().run();
      },
      editorUndo() {
        this.editor?.chain().focus().undo().run();
      },
      editorRedo() {
        this.editor?.chain().focus().redo().run();
      },

      // ── Active state getters ───────────────────────────────
      get isBold() {
        return this.editor?.isActive("bold") || false;
      },
      get isItalic() {
        return this.editor?.isActive("italic") || false;
      },
      get isUnderline() {
        return this.editor?.isActive("underline") || false;
      },
      get isH2() {
        return this.editor?.isActive("heading", { level: 2 }) || false;
      },
      get isH3() {
        return this.editor?.isActive("heading", { level: 3 }) || false;
      },
      get isBulletList() {
        return this.editor?.isActive("bulletList") || false;
      },
      get isOrderedList() {
        return this.editor?.isActive("orderedList") || false;
      },
      get isBlockquote() {
        return this.editor?.isActive("blockquote") || false;
      },

      destroy() {
        this.editor?.destroy();
        this.editor = null;
      }
    };
  });
}
